import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

export const useMapAnimationStore = defineStore('mapAnimationStore', () => {
    const defaultTimesSeriesSettings = { start: 'now' };

    const displayAnimationControls = ref(false);
    const animationFrames = ref([]);
    const frameCount = ref(0);
    const currentFrame = ref(0);
    const startFrame = ref(0);
    const timestep = ref(0);
    const isAnimating = ref(false);
    const isLoading = ref(true);
    const resumeAnimation = ref(false);
    const loadingMessage = ref('Loading...');
    const displayNowLine = ref(false);
    const timeseriesSettings = ref({ start: 'now'});

    const currentFrameTimestamp = computed(() => {
      return animationFrames.value[currentFrame.value];
    });

    function toggleAnimationStatus(settings) {
      const defaultSettings = {
        resume: false,
        restart: false
      }

      const { resume, restart } = Object.assign(defaultSettings, settings);

      isAnimating.value = !isAnimating.value;
      resumeAnimation.value = resume;

      window.$EventBus.dispatchEvent('animate-map', { animate: isAnimating.value, restart: restart });
    }

    function goToStartFrame() {
      currentFrame.value = 
        timeseriesSettings.value.start.indexOf('now') > -1
          ? startFrame.value 
          : frameCount.value === 0 
          ? 0 
          : frameCount.value - 1

      dispatchAnimationSliderChanged(currentFrame.value, isAnimating.value)
    }

    function setTimeseriesSettings(settings) {
      timeseriesSettings.value = settings;
    }

    function resetTimeseriesSettings() {
      timeseriesSettings.value = defaultTimesSeriesSettings;
    }

    function clearAnimationFrames() {
      animationFrames.value = [];
      frameCount.value = 0;
      currentFrame.value = 0;
      isLoading.value = true;
      isAnimating.value = false;
    }

    function dispatchAnimationSliderChanged(currentFrame, isAnimating) {
      if (!displayAnimationControls.value) return;
      
      window.$EventBus.dispatchEvent('animation-slider-changed', {
        currentFrame: currentFrame,
        animate: isAnimating,
        timeseries: false
      })
    }

    window.$EventBus.addEventListener('display-animation-controls', (e) => {
      displayAnimationControls.value = e.detail.displayAnimationControls
    });
    
    window.$EventBus.addEventListener('animation-frames-updated', (e) => {
      if (import.meta.env.DEV) console.log('--JWX-ANIMATION-FRAMES-UPDATED', e);

      if (!e.detail.animationFrames || e.detail.animationFrames.length === 0) return

      const newTimeFrames = e.detail.animationFrames;
      const previousFrame = currentFrame.value;
      let isCurrentFrameSet = false;

      if (
        animationFrames.value.length > 0 &&
        frameCount.value === newTimeFrames.length &&
        new Date(newTimeFrames[frameCount.value - 1]).getTime() === new Date(animationFrames.value[frameCount.value - 1]).getTime()
      ) {
        isCurrentFrameSet = true;
      }
      
      frameCount.value = newTimeFrames.length
      isLoading.value = true;

      const animationFramesWithTimestamps = [];
      let minTimeDelta = Infinity;

      newTimeFrames.forEach((frame, i, frames) => {
        const now = Date.now()
        const currentFrameTime = new Date(frame).getTime()
        const timeDelta = currentFrameTime - now

        if (timeseriesSettings.value.future === 'exclude' && currentFrameTime > now) {
          if (isCurrentFrameSet) return

          frameCount.value = i;
          displayNowLine.value = false
          isCurrentFrameSet = true
        }
          
        animationFramesWithTimestamps.push(frame)

        if (isCurrentFrameSet) return;

        // Set current frame to the frame closest to now or the timeseries settings start time (timeseriesSettings.value.start)
        // If map does not have a forecast do not use nowline
        if (Math.abs(timeDelta) < minTimeDelta) {
          if (timeseriesSettings.value.start === 'now-past' && currentFrameTime > now) return

          if (i + 1 < frames.length) {
            startFrame.value = i
            currentFrame.value = i
            displayNowLine.value = true

            minTimeDelta = Math.abs(timeDelta)
          } else {
            startFrame.value = i
            currentFrame.value = i
            displayNowLine.value = false
            isCurrentFrameSet = true
          }
        } else {
          isCurrentFrameSet = true;
        }
      });
      
      animationFrames.value = animationFramesWithTimestamps;
      isLoading.value = false;

      if (previousFrame !== currentFrame.value) {
        dispatchAnimationSliderChanged(currentFrame.value, isAnimating.value);
      }
    });

    window.$EventBus.addEventListener('animation-frame-changed', (e) => {
      if (import.meta.env.DEV) console.log('ANIMATION-FRAME-CHANGED', e)
      currentFrame.value = e.detail.currentFrame;
    });

    window.$EventBus.addEventListener('spot-detail-set-requested', (e) => {
      isLoading.value = e.detail.loading;
      loadingMessage.value = !e.detail.loading ? 'Loading...' : e.detail.message
    })

    return {
      displayAnimationControls,
      animationFrames,
      frameCount,
      currentFrame,
      timestep,
      isAnimating,
      currentFrameTimestamp,
      isLoading,
      resumeAnimation,
      loadingMessage,
      displayNowLine,
      timeseriesSettings,
      clearAnimationFrames,
      toggleAnimationStatus,
      setTimeseriesSettings,
      resetTimeseriesSettings,
      goToStartFrame
    }
})
